import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import type { MemberPointTypeDto, UpdateMemberPointTypeDto } from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import { errorMessage } from './ledger/errorMessage';
import { BUTTON, INPUT, PANEL } from './ledger/walletShared';

export function PointTypeCatalog() {
  const { t } = useTranslation('member');
  const canManage = useAuthStore((s) => s.allowedActions?.includes('member:asset_type_manage'));
  const { data, error } = useQuery<MemberPointTypeDto[]>({
    queryKey: ['point-types'],
    queryFn: () => api.get('/members/point-types'),
    retry: (count, err) => !(isAxiosError(err) && err.response?.status === 409) && count < 2,
  });
  if (isAxiosError(error) && error.response?.status === 409) return null;
  return (
    <section className={`${PANEL} mt-8`}>
      <h2 className="text-lg font-semibold">{t('pointTypesTitle')}</h2>
      {error && <p role="alert">{t('ledgerLoadFailed')}</p>}
      <div className="space-y-4">
        {data?.map((type) => (
          <PointTypeRow key={type.id} type={type} canManage={!!canManage} />
        ))}
      </div>
    </section>
  );
}
function PointTypeRow({ type, canManage }: { type: MemberPointTypeDto; canManage: boolean }) {
  const { t } = useTranslation('member');
  const qc = useQueryClient();
  const [draft, setDraft] = useState<UpdateMemberPointTypeDto | null>(null);
  const value = draft ?? type;
  const [error, setError] = useState('');
  const save = useMutation({
    mutationFn: (input: { venue: boolean; body: object }) =>
      api.patch(`/members/point-types/${type.id}${input.venue ? '/venue' : ''}`, input.body),
    onSuccess: async () => {
      setDraft(null);
      setError('');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['point-types'] }),
        qc.invalidateQueries({ queryKey: ['member-ledger-summary'] }),
      ]);
    },
    onError: (err) => setError(errorMessage(err, t('ledgerLoadFailed'))),
  });
  const edit = (fields: Partial<UpdateMemberPointTypeDto>) =>
    setDraft({
      displayName: value.displayName,
      sortOrder: value.sortOrder,
      showOnCard: value.showOnCard,
      isActive: value.isActive,
      ...fields,
    });
  return (
    <form
      className="border border-base-300 rounded-lg p-3 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (draft) save.mutate({ venue: false, body: draft });
      }}
    >
      <p className="text-sm text-base-content/60">{type.displayName}</p>
      <fieldset disabled={!canManage || save.isPending} className="grid gap-3 sm:grid-cols-2">
        <label>
          {t('pointTypeName')}
          <input
            className={INPUT}
            value={value.displayName}
            maxLength={100}
            required
            onChange={(e) => edit({ displayName: e.target.value })}
          />
        </label>
        <label>
          {t('pointTypeOrder')}
          <input
            className={INPUT}
            type="number"
            min={0}
            max={10000}
            value={value.sortOrder}
            onChange={(e) => edit({ sortOrder: Number(e.target.value) })}
          />
        </label>
        <label>
          <input
            type="checkbox"
            checked={value.showOnCard}
            onChange={(e) => edit({ showOnCard: e.target.checked })}
          />{' '}
          {t('pointTypeCard')}
        </label>
        <label>
          <input
            type="checkbox"
            checked={value.isActive}
            onChange={(e) => edit({ isActive: e.target.checked })}
          />{' '}
          {t('pointTypeActive')}
        </label>
        {canManage && (
          <button className={BUTTON} disabled={!draft || !value.displayName.trim()}>
            {t('ledgerSave')}
          </button>
        )}
      </fieldset>
      <label className="block">
        <input
          type="checkbox"
          disabled={!canManage || save.isPending}
          checked={type.enabled}
          onChange={(e) => save.mutate({ venue: true, body: { enabled: e.target.checked } })}
        />{' '}
        {t('pointTypeVenue')}
      </label>
      <p className="text-sm text-base-content/60">{t('pointTypeGrantOnly')}</p>
      {error && (
        <p role="alert" className="text-error">
          {error}
        </p>
      )}
    </form>
  );
}
